import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ModalRef } from '@app/core/services/core-modal.service';
import { DxDataGridComponent } from 'devextreme-angular';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Column } from 'devextreme/ui/data_grid';
import { createExcelWorkbook } from '@app/core/utils/exceljs.util';
import { saveFile } from '@app/core/utils/file-saver.util';
import { finalize, Observable } from 'rxjs';

export interface CoreGridModalData<T = any> {
  dataSource?: T[] | any;
  loadData?: () => Observable<T[]>;
  columns: CoreGridModalColumn[];
  noDataText?: string;
  remoteOperations?: boolean;
  showBorders?: boolean;
  showRowLines?: boolean;
  allowColumnReordering?: boolean;
  columnAutoWidth?: boolean;
  wordWrapEnabled?: boolean;
  pageSize?: number;
  pagingEnabled?: boolean;
  pagerVisible?: boolean;
  allowedPageSizes?: number[];
  exportFileName?: string;
  worksheetName?: string;
  closeAfterExport?: boolean;
  closeButtonText?: string;
  exportButtonText?: string;
  currencyField?: string;
  renderingColumnFunctions?: { [key: string]: (data?:any,row?:any)=>string };
  onLoadError?: (error: unknown) => void;
  onLinkClick?: (value: any, rowData?: T) => void;
  actionButtonText?: string;
}

export type CoreGridModalColumn = Column;

@Component({
    selector: 'core-grid-modal',
    templateUrl: './core-grid-modal.component.html',
    styleUrls: ['./core-grid-modal.component.scss'],
    standalone: false
})
export class CoreGridModalComponent<T = any> implements OnInit {
  @Input() modalData!: CoreGridModalData<T>;
  @Input() modalRef!: ModalRef;

  @ViewChild('grid') grid?: DxDataGridComponent;

  dataSource: T[] | any = [];
  loading = false;
  private dimensionsUpdated = false;

  ngOnInit(): void {
    this.dataSource = this.modalData.dataSource ?? [];

    if (this.modalData.loadData) {
      this.loadGridData();
    }
  }

  get showExportButton(): boolean {
    return !!this.modalData.exportFileName;
  }

  get hasRows(): boolean {
    return Array.isArray(this.dataSource) ? this.dataSource.length > 0 : !!this.dataSource;
  }

  closeModal() {
    this.modalRef.close();
  }
  execRenderingFunctions(cellInfo:any) {
    const fieldName = cellInfo.column?.dataField ?? ''
    if(fieldName==='') return
    const fn = this.modalData.renderingColumnFunctions?.[fieldName]
    if(!fn) return ''
    return fn(cellInfo.data,cellInfo.row)
  }
  async downloadGrid() {
    if (!this.grid) return;

    const workbook = await createExcelWorkbook();
    const worksheet = workbook.addWorksheet(this.modalData.worksheetName ?? 'Report');

    exportDataGrid({
      component: this.grid.instance,
      worksheet,
    }).then(() => {
      workbook.xlsx.writeBuffer().then(async (buffer) => {
        await saveFile(
          new Blob([buffer], { type: 'application/octet-stream' }),
          this.modalData.exportFileName!
        );

        if (this.modalData.closeAfterExport) {
          this.closeModal();
        }
      });
    });
  }

  onContentReady() {
    if (this.dimensionsUpdated) return;

    this.dimensionsUpdated = true;
    setTimeout(() => this.grid?.instance.updateDimensions());
  }

  openLink(value: any, rowData?: T) {
    this.modalData.onLinkClick?.(value, rowData);
  }

  selectRow(rowData: T) {
    this.modalRef.close(rowData);
  }

  private loadGridData() {
    this.loading = true;

    this.modalData.loadData!()
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (data) => {
          this.dataSource = data;
          setTimeout(() => this.grid?.instance.updateDimensions());
        },
        error: (error) => {
          this.modalData.onLoadError?.(error);
        }
      });
  }
}
